import { transactionsApiTemplateSlice as api } from '../state/transactions/transactionsApiTemplateSlice';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTransactionsV2: build.query<
      GetTransactionsV2ApiResponse,
      GetTransactionsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/transactions`,
        params: {
          pageSize: queryArg.pageSize,
          pageNumber: queryArg.pageNumber,
          vehicleId: queryArg.vehicleId,
          vehicleGroupId: queryArg.vehicleGroupId,
          serviceType: queryArg.serviceType,
          currency: queryArg.currency,
          fromCreatedAt: queryArg.fromCreatedAt,
          toCreatedAt: queryArg.toCreatedAt,
          fromDisplayDate: queryArg.fromDisplayDate,
          toDisplayDate: queryArg.toDisplayDate,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetTransactionsV2ApiResponse =
  /** status 200 OK */ TransactionsList;
export type GetTransactionsV2ApiArg = {
  /** Page size between 1 and 1000 */
  pageSize: string;
  /** Zero-based page index */
  pageNumber: string;
  vehicleId?: string[];
  /** UUID or "unassigned" */
  vehicleGroupId?: string[];
  serviceType?: ServiceType[];
  currency?: Currency[];
  fromCreatedAt?: string;
  toCreatedAt?: string;
  fromDisplayDate?: string;
  toDisplayDate?: string;
};
export type PageResponse = {
  totalPages: number;
  totalItems: number;
};
export type Address = {
  street: string;
  zipCode: string;
  city: string;
  state?: string;
  /** A 2-digit country code, conforming to ISO 3166 Alpha-2 notation */
  country: string;
};
export type Location = {
  /** Reference to the Point of Interest from SimplePay's PoiService */
  id?: string;
  name: string;
  brand?: string;
  address: Address;
};
export type FuelingDetails = {
  pumpNumber: string;
};
export type PaymentDetails = {
  paymentMethod: PaymentMethodType;
  paymentPartner: PaymentPartnerType;
  lastFourDigits: string;
};
export type GeoCoordinates = {
  latitude: string;
  longitude: string;
};
export type VehicleDetails = {
  id: string;
  driverId?: string;
  /** Current mileage of the vehicle */
  mileage?: string;
  /** Position of the vehicle when transaction was started */
  vehicleLocation?: GeoCoordinates;
};
export type ChargingDetails = {
  plugType?: string;
  plugEvseId?: string;
  /** Duration of the charging process denoted in HH:MM:SS */
  duration?: string;
};
export type MonetaryValue = string;
export type Currency = string;
export type TransactionAmount = {
  amountNet: MonetaryValue;
  amountGross: MonetaryValue;
  amountVat: MonetaryValue;
  currency: Currency;
};
export type TransactionItem = {
  description: string;
  product?: Product;
  quantity: string;
  unit: UnitType;
  deliveryUnitAmount: TransactionAmount;
  deliveryAmount: TransactionAmount;
  dashboardAmount?: TransactionAmount & any;
  completionAmount?: TransactionAmount;
  vatRate: string;
};
export type Transaction = {
  id: string;
  referenceId?: string;
  serviceType: ServiceType;
  servicePartner: ServicePartner;
  state: TransactionState;
  cancelationState?: TransactionCancelationState;
  errorCode?: TransactionErrorCode;
  locationDetails?: Location;
  fuelingDetails?: FuelingDetails;
  paymentDetails: PaymentDetails;
  vehicleDetails: VehicleDetails;
  chargingDetails?: ChargingDetails;
  deliveryAmount?: TransactionAmount;
  completionAmount?: TransactionAmount;
  dashboardAmount?: TransactionAmount;
  items: TransactionItem[];
  /** Timestamp when transaction was delivered. The transaction state changed to DELIVERED. */
  deliveredAt?: string;
  /** Timestamp when transaction was completed. The transaction state changed to COMPLETED. */
  completedAt?: string;
  /** Timestamp when transaction was created. */
  createdAt: string;
  /** Timestamp when transaction was updated. */
  updatedAt?: string;
  /** `completedAt` for completed transactions, `deliveredAt` for delivered transactions or `createdAt` for all
    other transactions.
     */
  displayDate: string;
};
export type TransactionsList = PageResponse & {
  items: Transaction[];
};
export type ErrorResponse = {
  message: string;
  errorCode: ErrorCode;
  fields: string[];
  /** Name of the vehicle the card is already linked to. Only present for CARD_ALREADY_IN_USE_SAME_ACCOUNT. */
  vehicleName?: string;
};
export enum ServiceType {
  Fueling = 'FUELING',
  Breakdown = 'BREAKDOWN',
  Parking = 'PARKING',
  Washing = 'WASHING',
  Charging = 'CHARGING',
  Other = 'OTHER',
}
export enum ServicePartner {
  MockFuelingPartner = 'MOCK_FUELING_PARTNER',
  MockBreakdownPartner = 'MOCK_BREAKDOWN_PARTNER',
  MockRoadsidePartner = 'MOCK_ROADSIDE_PARTNER',
  Mobile24 = 'MOBILE_24',
  Travis = 'TRAVIS',
  Uta = 'UTA',
  Pace = 'PACE',
  ManChargeAndGo = 'MAN_CHARGE_AND_GO',
}
export enum TransactionState {
  Created = 'CREATED',
  InProgress = 'IN_PROGRESS',
  Delivered = 'DELIVERED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Canceled = 'CANCELED',
}
export enum TransactionCancelationState {
  Triggered = 'TRIGGERED',
  Failed = 'FAILED',
  Successful = 'SUCCESSFUL',
}
export enum TransactionErrorCode {
  TransactionCreationFailed = 'TRANSACTION_CREATION_FAILED',
  LocationNotInRange = 'LOCATION_NOT_IN_RANGE',
  InvalidPaymentMethod = 'INVALID_PAYMENT_METHOD',
  SelectionNotExisting = 'SELECTION_NOT_EXISTING',
  SelectionNotAvailable = 'SELECTION_NOT_AVAILABLE',
  TransactionProcessFailed = 'TRANSACTION_PROCESS_FAILED',
  PreAuthorizationFailed = 'PRE_AUTHORIZATION_FAILED',
  PaymentFailed = 'PAYMENT_FAILED',
}
export enum PaymentMethodType {
  FuelCard = 'FUEL_CARD',
  ChargeCard = 'CHARGE_CARD',
}
export enum PaymentPartnerType {
  MockFuelingPayment = 'MOCK_FUELING_PAYMENT',
  MockUtaPayment = 'MOCK_UTA_PAYMENT',
  UtaPayment = 'UTA_PAYMENT',
  DkvPayment = 'DKV_PAYMENT',
  ManChargeAndGoPayment = 'MAN_CHARGE_AND_GO_PAYMENT',
}
export enum Product {
  Adblue = 'ADBLUE',
  AdblueJerrycan = 'ADBLUE_JERRYCAN',
  BreakdownTyres = 'BREAKDOWN_TYRES',
  BridgeFee = 'BRIDGE_FEE',
  Other = 'OTHER',
  ChargingBlockingFee = 'CHARGING_BLOCKING_FEE',
  ChargingSession = 'CHARGING_SESSION',
  EngineOil = 'ENGINE_OIL',
  FerryFare = 'FERRY_FARE',
  Tax = 'TAX',
  Diesel = 'DIESEL',
  BioDiesel = 'BIO_DIESEL',
  Hvo30Diesel = 'HVO30_DIESEL',
  HeatingOil = 'HEATING_OIL',
  PremiumDiesel = 'PREMIUM_DIESEL',
  Petrol = 'PETROL',
  SuperE5 = 'SUPER_E5',
  PremiumPetrol = 'PREMIUM_PETROL',
  Mix = 'MIX',
  SuperE10 = 'SUPER_E10',
  SuperE85 = 'SUPER_E85',
  LiquidPetrolGas = 'LIQUID_PETROL_GAS',
  NaturalGas = 'NATURAL_GAS',
  LiquidNaturalGas = 'LIQUID_NATURAL_GAS',
  LiquidBioGas = 'LIQUID_BIO_GAS',
  Hydrogen = 'HYDROGEN',
  GearOil = 'GEAR_OIL',
  PartsAndAccessories = 'PARTS_AND_ACCESSORIES',
  GeneralMaintenance = 'GENERAL_MAINTENANCE',
  VehicleParts = 'VEHICLE_PARTS',
  TowingService = 'TOWING_SERVICE',
  Insurance = 'INSURANCE',
  TollingFee = 'TOLLING_FEE',
  TunnelFee = 'TUNNEL_FEE',
  UtilizationServiceFee = 'UTILIZATION_SERVICE_FEE',
  ParkingFee = 'PARKING_FEE',
  VehicleCleaning = 'VEHICLE_CLEANING',
  BreakdownRepair = 'BREAKDOWN_REPAIR',
  BreakdownServiceFee = 'BREAKDOWN_SERVICE_FEE',
}
export enum UnitType {
  Hours = 'HOURS',
  Kg = 'KG',
  Kwh = 'KWH',
  Liter = 'LITER',
  Pieces = 'PIECES',
}
export enum ErrorCode {
  InvalidPathParameters = 'INVALID_PATH_PARAMETERS',
  InvalidQueryParameters = 'INVALID_QUERY_PARAMETERS',
  InvalidPayload = 'INVALID_PAYLOAD',
  InvalidFuelCard = 'INVALID_FUEL_CARD',
  InvalidStatusChange = 'INVALID_STATUS_CHANGE',
  TransactionStateNotCancelable = 'TRANSACTION_STATE_NOT_CANCELABLE',
  PoiNotFound = 'POI_NOT_FOUND',
  OnlinePaymentNotSupported = 'ONLINE_PAYMENT_NOT_SUPPORTED',
  PaymentMethodNotDeletable = 'PAYMENT_METHOD_NOT_DELETABLE',
  PaymentMethodNotAssignable = 'PAYMENT_METHOD_NOT_ASSIGNABLE',
  VehicleNotFound = 'VEHICLE_NOT_FOUND',
  VehicleServiceNotActivated = 'VEHICLE_SERVICE_NOT_ACTIVATED',
  VehicleDoesNotHaveLicensePlate = 'VEHICLE_DOES_NOT_HAVE_LICENSE_PLATE',
  PaymentMethodNotFound = 'PAYMENT_METHOD_NOT_FOUND',
  PaymentMethodNotUsableAtLocation = 'PAYMENT_METHOD_NOT_USABLE_AT_LOCATION',
  BookingNotCancelable = 'BOOKING_NOT_CANCELABLE',
  CardAlreadyInUseSameAccount = 'CARD_ALREADY_IN_USE_SAME_ACCOUNT',
  CardAlreadyInUseOtherAccount = 'CARD_ALREADY_IN_USE_OTHER_ACCOUNT',
  UnknownError = 'UNKNOWN_ERROR',
}
export const { useGetTransactionsV2Query } = injectedRtkApi;
